import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { getAppParams, getUrlParams } from "../apiService/timespam";
import { lifestyleApi } from "../apiService/api";
import "./Theme.css";
import "./HealthTest.css";

const buildNavUrl = (encoded, uid, extraParams = {}) => {
  const decoded = decodeURIComponent(encoded);
  const faiStart = decoded.indexOf("fai=") + 4;
  const faiParam = decoded.substring(faiStart);
  const faiObj = JSON.parse(decodeURIComponent(faiParam));
  // encodeURIComponent on each value so base64 uid (contains "=") and other
  // special characters don't break query-string parsing in the destination URL.
  const queryStr = Object.entries({ uid, ...extraParams })
    .filter(([, v]) => v !== "" && v !== null && v !== undefined)
    .map(([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`)
    .join("&");
  faiObj.url = `${faiObj.url}?${queryStr}`;
  return decoded.replace(faiParam, encodeURIComponent(JSON.stringify(faiObj)));
};

const HealthTest = () => {
  const { t } = useTranslation();
  const {
    uid, goqiiUserId,
    nonce, apiKey, signature, goqiiAccessToken,
    appVersion, appType,
  } = getAppParams();
  const { theme, lang } = getUrlParams();

  const [features, setFeatures] = useState({
    health_risk_assessment: true,
    cognitive_age_assessment: true,
    mental_wellness_assessment: true,
    skinalyze: true,
    addiction_assessments: true,
    side_effects_tracker: false,
    intelli_bmi: false,
  });

  useEffect(() => {
    if (!goqiiUserId) return;
    lifestyleApi
      .fetchHealthAssessmentAccess(goqiiUserId)
      .then((res) => {
        const data = res?.data?.data;
        if (data?.features) {
          setFeatures((prev) => ({ ...prev, ...data.features }));
        } else if (data?.status) {
          setFeatures((prev) => ({
            ...prev,
            side_effects_tracker: true,
            intelli_bmi: true,
          }));
        }
      })
      .catch((err) => console.error("fetchHealthAssessmentAccess:", err));
  }, [goqiiUserId]);

  // Forward all auth + UI params so destination pages (AllGamesPlay, assessments)
  // can call their APIs without needing a separate auth mechanism.
  const forwardedParams = {
    goqiiUserId, nonce, apiKey, signature, goqiiAccessToken,
    appVersion, appType,
    theme, lang,
  };

  // ---- Cognitive Age ----
  const isSpecialCogUser = ["5976538", "114495", "4639449"].includes(goqiiUserId);
  const cogBaseUrl = isSpecialCogUser
    ? "https://goqii.com/app?fsn=223&fssn=0&fai=%7B%20%22url%22%3A%20%22https%3A%5C%2F%5C%2Flifestyle1.goqii.com%5C%2Fcognitive-assessment-home%22%2C%22title%22%3A%20%22Cognitive%20Age%22%20%7D"
    : "https://goqii.com/app?fsn=223&fssn=0&fai=%7B%20%22url%22%3A%20%22https%3A%5C%2F%5C%2Flifestyle1.goqii.com%5C%2Fgames-4-good%22%2C%22title%22%3A%20%22Cognitive%20Age%22%20%7D";
  const updatedUrlCog = buildNavUrl(cogBaseUrl, uid, { gameMode: "assessment", ...forwardedParams });

  // ---- Mental Wellness ----
  const updatedUrlMental = buildNavUrl(
    "https://goqii.com/app?fsn=223&fssn=0&fai=%7B%20%22url%22%3A%20%22https%3A%2F%2Flifestyle1.goqii.com%2Fhealth_assessment%22%2C%22title%22%3A%20%22Mental%20Wellness%20Assessment%22%20%7D",
    uid,
    forwardedParams
  );

  // ---- Addiction ----
  const updatedUrlAddiction = buildNavUrl(
    "https://goqii.com/app?fsn=223&fssn=0&fai=%7B%22url%22%3A%22https%3A%2F%2Flifestyle1.goqii.com%2Faddiction_assessment%22%2C%22title%22%3A%22Addiction%20Assessment%22%7D",
    uid,
    forwardedParams
  );

  // ---- Skin ----
  const updatedUrlSkin = buildNavUrl(
    "https://goqii.com/app?fsn=223&fssn=0&fai=%7B%20%22url%22%3A%20%22https%3A%2F%2Flifestyle1.goqii.com%2Fskin_assessment%22%2C%22title%22%3A%20%22Skin%20Wellness%20Assessment%22%20%7D",
    uid,
    forwardedParams
  );

  // ---- Side Effects ----
  const updatedUrlSideeffect = buildNavUrl(
    "https://goqii.com/app?fsn=223&fssn=0&fai=%7B%22url%22%3A%22https%3A%2F%2Freferral.goqii.com%2Fside_effects_tracker%22%2C%22title%22%3A%22Side%20Effects%20Tracker%22%7D",
    uid,
    forwardedParams
  );

  const allAssessments = [
    {
      key: "side_effects_tracker",
      category: t("healthTest.sideEffects.title"),
      desc: t("healthTest.sideEffects.desc"),
      icon: "https://appcdn.goqii.com/storeimg/15447_1781851229.png",
      url: updatedUrlSideeffect,
      accent: "#14b8a6",
      accentSoft: "rgba(20,184,166,0.12)",
    },
    {
      key: "health_risk_assessment",
      category: t("healthTest.hra.title"),
      desc: t("healthTest.hra.desc"),
      icon: "https://appcdn.goqii.com/storeimg/21983_1778484667.png",
      url: "https://goqii.com/app?fsn=12&fssn=0",
      accent: "#22c55e",
      accentSoft: "rgba(34,197,94,0.12)",
    },
    {
      key: "cognitive_age_assessment",
      category: t("healthTest.cognitive.title"),
      desc: t("healthTest.cognitive.desc"),
      icon: "https://appcdn.goqii.com/storeimg/73829_1778484701.png",
      url: updatedUrlCog,
      accent: "#6366f1",
      accentSoft: "rgba(99,102,241,0.12)",
    },
    {
      key: "mental_wellness_assessment",
      category: t("healthTest.mental.title"),
      desc: t("healthTest.mental.desc"),
      icon: "https://appcdn.goqii.com/storeimg/50700_1778484736.png",
      url: updatedUrlMental,
      accent: "#8b5cf6",
      accentSoft: "rgba(139,92,246,0.12)",
    },
    {
      key: "skinalyze",
      category: t("healthTest.skin.title"),
      desc: t("healthTest.skin.desc"),
      icon: "https://appcdn.goqii.com/storeimg/92600_1778484765.png",
      url: updatedUrlSkin,
      accent: "#ec4899",
      accentSoft: "rgba(236,72,153,0.12)",
    },
    {
      key: "addiction_assessments",
      category: t("healthTest.addiction.title"),
      desc: t("healthTest.addiction.desc"),
      icon: "https://appcdn.goqii.com/storeimg/96315_1778484783.png",
      url: updatedUrlAddiction,
      accent: "#ef4444",
      accentSoft: "rgba(239,68,68,0.12)",
    },
    {
      key: "intelli_bmi",
      category: t("healthTest.bmi.title"),
      desc: t("healthTest.bmi.desc"),
      icon: "https://appcdn.goqii.com/storeimg/97753_1781851243.png",
      url: "https://goqii.com/app?fsn=248&fssn=0",
      accent: "#f59e0b",
      accentSoft: "rgba(245,158,11,0.12)",
    },
  ];

  const assessments = allAssessments.filter((item) => features[item.key]);

  const handleCardClick = (url) => {
    window.open(url);
  };

  return (
    <div className="ht-wrapper">
      <div className="ht-grid">
        {assessments.map((item, idx) => (
          <button
            key={idx}
            className="ht-card"
            style={{ "--accent": item.accent, "--accent-soft": item.accentSoft }}
            onClick={() => handleCardClick(item.url)}
          >
            <div className="ht-icon-wrap">
              <img src={item.icon} alt={item.category} className="ht-icon" />
            </div>
            <div className="ht-card-text">
              <p className="ht-card-title">{item.category}</p>
              <p className="ht-card-desc">{item.desc}</p>
            </div>
            <div className="ht-chevron">›</div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default HealthTest;
